/**
 * Die Ratenzeilen und die Ratensumme des Abschnitts „Zahlstatus" - Portierung von `renderPayments`
 * (`index.html:9325-9395`) und `renderPaySum` (`index.html:9398-9419`), samt den beiden Behaeltern,
 * in die der Bestand sie schreibt (`<div class="payment-status-list" id="payRows">` und
 * `<div class="pay-sum" id="paySum">`, `index.html:8662-8663`).
 *
 * ================= Seit dem Nachtrag 01.10.2026 (Plan 2026-10-02, Aufgabe 3) ====================
 *
 * Der Bestand unterschied „Anzahlung + Restzahlung" und „Nur Gesamtzahlung" (`paymentModeOf`). Das ist
 * keine fachliche Unterscheidung, sondern dieselbe Ratenliste mit einer oder zwei Zeilen. Jetzt gilt:
 *
 * - Die Raten werden durchgehend 01 ... n nummeriert, der Titel kommt aus der POSITION
 *   (`ratenBezeichnung`: Gesamtzahlung / Anzahlung / Zwischenzahlung / Restzahlung), nie aus `label`.
 * - Das Anteilsfeld steht an JEDER Rate (im Bestand nur an den ersten zwei im Split-Modus).
 * - `Ratenplan` zeigt einen Plan „Nach Plan": Anteil, Betrag, Anker, Tage und Faelligkeit sind nur
 *   Anzeige; Zahlstatus, Bezahlt am und Dollarkurs bleiben bedienbar (Einzel-PATCH ueber `onRate`).
 * - `PlanEditor` ist der individuelle Editor: die Planfelder bearbeitet der Nutzer in einem lokalen
 *   ENTWURF, ein Knopf „Speichern" schickt den ganzen Plan atomar (`PUT payment-plan`). Zahlstatus
 *   und Dollarkurs gehen weiterhin sofort einzeln - sie sind Ausfuehrung, nicht Plan.
 * - Der Hinweis unter „Zahlstatus" folgt dem Status: bezahlt ohne Datum zeigt „Bezahlt", nicht „Noch offen".
 *
 * ================= Woertlich uebernommene Eigenheiten ==========================================
 *
 * 1. **Die Ratenzeile ist eine KARTE, keine Tabellenzeile.** `renderPayments` baut
 *    `document.createElement('div')` mit `class="payment-status-card status-…"`. Ein Test, der
 *    `getByRole("row", …)` sucht, findet hier nichts (siehe `zahlungTab.test.tsx`, Abweichung 4).
 * 2. **Der Nachschlag `PAYMENT_STATUSES[status].className` ist ungesichert** (`index.html:9338`).
 *    `paymentStatusOf` laesst einen Prototyp-Schluessel nach aussen (Befund 8), und
 *    `PAYMENT_STATUSES['toString']` ist wahrhaftig - `.className` daran ist `undefined`, die Karte
 *    traegt dann `status-undefined`. Kein Absturz, aber auch keine Statusfarbe. Nicht abgesichert.
 * 3. **Der Platzhalter des Betragsfelds ist eine ROHE Zahl**, kein formatierter Betrag:
 *    `esc(Math.round(payAmount(o, p) * 100) / 100)` (`index.html:9360`) - also „1227.6", waehrend
 *    die Hilfszeile darunter „$ 1.227,6" zeigt (`fmtUsd`). Zwei Schreibweisen desselben Werts
 *    unmittelbar nebeneinander; woertlich uebernommen.
 * 4. **`renderPaySum` rechnet die Prozentsumme ueber ALLE Raten.** Der „offen"-Betrag ist
 *    `Gesamt − bezahlt`, nicht `orderOpenPaymentAmount(o)` (das ueber `!p.paid` statt ueber
 *    `paymentStatusOf` filtert) - zwei Wege zum selben Zweck, die bei einer Rate mit `status: 'paid'`
 *    und `paid: false` auseinander laufen. Hier steht der Weg von `renderPaySum`.
 *
 * `addPayment` (`:9470`) und `removePayment` (`:9497`) haben im Bestand KEINEN Aufrufer; „Rate
 * hinzufuegen" / „Rate loeschen" des Editors sind deshalb eine bewusste Erweiterung (Spec,
 * „Individueller Editor"), kein Nachbau.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Datumsfeld } from "../Datumsfeld.tsx";
import { Ikone } from "../../Ikone.tsx";
import { PAYMENT_STATUSES } from "../../../config/payments.ts";
import {
  faelligkeitVonRate,
  fehlendesAnkerfeld,
  wartetAufAbruf,
  ANKER_LABEL,
  type Anker,
  type Ankerdaten,
  type Ankerergebnis,
} from "../../../lib/anker.ts";
import { payAmount, paymentStatusOf } from "../../../lib/payments.ts";
import { NUM, fmtDate, fmtUsd } from "../../../lib/format.ts";
import {
  alsPlanRaten,
  anteilFehlt,
  entwurfAusRaten,
  entwurfGeaendert,
  entwurfInhaltGleich,
  loeschenGesperrtGrund,
  planFeldSetzen,
  planSignatur,
  rateEntfernen,
  rateHinzufuegen,
  ratenBezeichnung,
  ratenNummer,
  speichernGesperrtGrund,
  summenzeile,
  type Aenderbar,
  type EntwurfRate,
  type PlanRateEingabe,
} from "../../../lib/zahlungsplanEditor.ts";
import { BezahltMarkieren, IstZahlungBlock, type IstZahlungAktionen } from "./IstZahlung.tsx";
import type { Order, OrderPayment, Supplier } from "../../../lib/types.ts";

/**
 * Die `basis` der Rechnung wird SICHTBAR gemacht, nicht nur mitgeführt: eine aus `etd_pi` gerechnete
 * Faelligkeit ist eine Annahme, und wer damit Liquiditaet plant, muss das der Zeile ansehen.
 */
function faelligkeitsKlasse(f: Ankerergebnis): string {
  if (f.basis === "prognose") return "faelligkeit-prognose";
  if (f.basis === "fehlt" || f.datum === null) return "faelligkeit-fehlt";
  return "";
}

function faelligkeitsText(f: Ankerergebnis, p: OrderPayment, o: Order): string {
  if (f.datum === null) {
    // Stock Order ohne DSV-Sendung: Abruf steht aus, kein Datum (Spec 2026-10-04).
    if (wartetAufAbruf(o as unknown as Ankerdaten, (p.anchor as Anker | null | undefined) ?? null)) {
      return "Stock Order – wartet auf Abruf";
    }
    // R6: das konkret fehlende Feld nennen (mit Frachtart), bei Handdatum zur Eingabe auffordern.
    const feld = fehlendesAnkerfeld(o as unknown as Ankerdaten, (p.anchor as Anker | null | undefined) ?? null);
    if (feld === null) return "Kein Datum – bitte eintragen";
    // R8: bei unverknuepfter Bestellung nennt der Hinweis den Grund.
    return feld === "ETD (PI)"
      ? "ETD (PI) fehlt – keine DSV-Verknüpfung"
      : `${feld} fehlt – keine Fälligkeit berechenbar`;
  }
  if (f.basis === "prognose") return `${fmtDate(f.datum)} · Prognose (ETD aus der PI)`;
  if (f.basis === "eingefroren") return `${fmtDate(f.datum)} · festgeschrieben`;
  if (f.basis === "hand") return `${fmtDate(f.datum)} · von Hand gesetzt`;
  return fmtDate(f.datum);
}

/** Der Hinweis unter „Zahlstatus": folgt dem Status, nicht dem Vorhandensein eines Datums. */
function statusHinweis(status: string, p: OrderPayment): string {
  if (status === "paid") {
    const datum = p.actual?.paidOn ?? (p.paidDate as string | undefined);
    return datum ? `Bezahlt am ${fmtDate(datum)}` : "Bezahlt";
  }
  if (status === "fixed") return "Zahlungsziel und Betrag sind fixiert";
  return "Noch offen";
}

/**
 * Eines der drei Zahlenfelder einer Ratenzeile (Anteil/`pct`, Zahlung USD/`amount`,
 * Dollarkurs/`usdRate`) - der Wert bleibt WAEHREND der Eingabe rein lokal und wandert erst beim
 * Verlassen des Feldes (`onBlur`) an `onCommit`.
 *
 * NACHBESSERUNG (Task 7, Zahlungsraten-Reparatur, Pruefrunde): jeder Tastendruck in einem dieser
 * Felder loeste einen kompletten Zyklus aus PATCH und anschliessendem vollen `GET /api/orders/:id`
 * aus; ein zweiter Tastendruck las die `rowVersion` noch aus dem alten Stand -> 409, und das
 * Nachladen schrieb das kontrollierte Feld MITTEN im Tippen neu. Der lokale Wert wird nur
 * nachgezogen, wenn das Feld GERADE NICHT fokussiert ist (`fokussiert`-Ref). `onCommit` feuert genau
 * einmal pro abgeschlossener Eingabe, und nur, wenn sich der Wert tatsaechlich geaendert hat.
 *
 * `sofort` (Editor): der Entwurf ist rein lokal, ohne Netz - dort darf jeder Tastendruck committen,
 * damit die Summenzeile mitlaeuft.
 */
function Zahlenfeld({
  wert,
  onCommit,
  min,
  max,
  step,
  placeholder,
  className,
  ariaLabel,
  gesperrt,
  sofort,
  ungueltig,
}: {
  wert: string;
  onCommit: (wert: string) => void;
  min?: string;
  max?: string;
  step: string;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
  gesperrt?: boolean;
  sofort?: boolean;
  ungueltig?: boolean;
}) {
  const [lokal, setLokal] = useState(wert);
  const fokussiert = useRef(false);

  useEffect(() => {
    if (!fokussiert.current) setLokal(wert);
  }, [wert]);

  return (
    <input
      type="number"
      className={className}
      aria-label={ariaLabel}
      aria-invalid={ungueltig ? true : undefined}
      disabled={gesperrt}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      value={lokal}
      onFocus={() => {
        fokussiert.current = true;
      }}
      onInput={(e) => {
        setLokal(e.currentTarget.value);
        if (sofort) onCommit(e.currentTarget.value);
      }}
      onChange={() => {}}
      onBlur={() => {
        fokussiert.current = false;
        if (lokal !== wert) onCommit(lokal);
      }}
    />
  );
}

/** Eine Ratenkarte. `planGesperrt`: Planfelder nur Anzeige. `onPlan`: Eingabe eines Planfelds. */
function Ratenkarte({
  p,
  index,
  anzahl,
  o,
  planGesperrt,
  onPlan,
  onAusfuehrung,
  ausfuehrungGesperrt,
  sofort,
  loeschen,
  anteilNachtragbar,
  istAktionen,
}: {
  p: OrderPayment;
  index: number;
  anzahl: number;
  o: Order;
  planGesperrt: boolean;
  onPlan: (feld: Aenderbar, wert: string) => void;
  onAusfuehrung: (feld: keyof OrderPayment, wert: string) => void;
  /** Eine noch nicht gespeicherte Rate hat keine Version: Status und Dollarkurs erst nach dem Speichern. */
  ausfuehrungGesperrt?: boolean;
  sofort?: boolean;
  loeschen?: { onClick: () => void; grund: string | null };
  /** Nicht offene Altrate ohne gespeicherten Anteil: nur dieses Feld bleibt eingebbar (Entscheidung 02.10.). */
  anteilNachtragbar?: boolean;
  /** Ist-Zahlung bedienen (nur mit `manage_payments`); ohne bleibt sie schreibgeschuetzt sichtbar. */
  istAktionen?: IstZahlungAktionen;
}) {
  const status = paymentStatusOf(p);
  // Bezahlt ohne Ist-Zahlung = Altbestand: der Server liefert das Kennzeichen (`ratenMitIstZahlung`). Nur Anzeige,
  // keine Bedienelemente - auch der Dollarkurs bleibt gesperrt.
  const historisch = p.historisch === true;
  const kannMarkieren =
    status !== "paid" && !!istAktionen && !ausfuehrungGesperrt && !!p.id && Number.isInteger(p.rowVersion);
  const faellig = faelligkeitVonRate(p, o);
  const number = ` ${ratenNummer(index)}`;
  const title = ratenBezeichnung(index, anzahl);
  const fehltAnteil = (!planGesperrt || !!anteilNachtragbar) && anteilFehlt(p.pct);
  const ausfuehrungHinweis = ausfuehrungGesperrt ? "Erst nach dem Speichern bearbeitbar" : undefined;
  // Siehe Eigenheit 2: ungesicherter Nachschlag, woertlich (kein `?.`).
  const klasse = PAYMENT_STATUSES[status].className;
  return (
    <div className={`payment-status-card status-${klasse}`}>
      <div className="payment-status-head">
        <div className="payment-status-title">
          <span>Zahlstatus{number}</span>
          <strong>{title}</strong>
        </div>
        <label className="payment-share" title="Anteil am Bestellwert" data-permission="manage_payments">
          Anteil
          <Zahlenfeld
            min="0"
            max="100"
            step="0.01"
            ariaLabel={`Anteil der Rate ${index + 1}`}
            wert={(p.pct as string) ?? ""}
            gesperrt={planGesperrt && !anteilNachtragbar}
            sofort={sofort}
            ungueltig={fehltAnteil}
            onCommit={(wert) => onPlan("pct", wert)}
          />
          %
        </label>
        {fehltAnteil ? <span className="payment-share-fehlt">Anteil fehlt</span> : null}
        {loeschen ? (
          <button
            className="row-del"
            type="button"
            data-permission="manage_payments"
            title={loeschen.grund ?? `Rate ${index + 1} löschen`}
            aria-label={`Rate ${index + 1} löschen`}
            disabled={loeschen.grund !== null}
            onClick={loeschen.onClick}
          >
            <Ikone name="trash-2" />
          </button>
        ) : null}
      </div>
      <div className="payment-status-grid">
        <div className="payment-status-field" data-permission="manage_payments">
          <label>Zahlung USD{number}</label>
          <div className="payment-money-input">
            <span>USD</span>
            <Zahlenfeld
              min="0"
              step="0.01"
              wert={(p.amount as string) ?? ""}
              gesperrt={planGesperrt}
              sofort={sofort}
              // Siehe Eigenheit 3: rohe Zahl, kein Geldformat.
              placeholder={p.pct ? String(Math.round(payAmount(o, p) * 100) / 100) : "0,00"}
              onCommit={(wert) => onPlan("amount", wert)}
            />
          </div>
          <div className="payment-status-help">
            {p.amount
              ? "Fester Zahlungsbetrag"
              : p.pct
                ? `${p.pct as string} % vom Bestellwert · ${fmtUsd(payAmount(o, p))}`
                : "Werteingabe in USD"}
          </div>
        </div>
        <div className="payment-status-field" data-permission="manage_payments">
          <label>Fixes Zahlungsziel{number}</label>
          <Datumsfeld
            className="mono"
            wert={(p.due as string) ?? ""}
            disabled={planGesperrt}
            title={planGesperrt ? "Teil des Plans – gesperrt" : undefined}
            onCommit={(wert) => onPlan("dueDate", wert)}
          />
          <div className={`payment-status-help ${faelligkeitsKlasse(faellig)}`}>
            {faelligkeitsText(faellig, p, o)}
          </div>
        </div>
        <div className="payment-status-field" data-permission="manage_payments">
          {/* Sichtbare Beschriftung OHNE htmlFor, dazu ein aria-label am <select> selbst:
              der sichtbare Text ("Anker 02") wiederholt sich ueber die Karten hinweg, das
              aria-label ("Anker der Rate 2") ist eindeutig. Beides zugleich als <label
              htmlFor> zu fuehren, gaebe zwei konkurrierende Namensquellen. */}
          <span className="payment-status-label">Anker{number}</span>
          <select
            aria-label={`Anker der Rate ${index + 1}`}
            value={(p.anchor as string) ?? ""}
            disabled={planGesperrt}
            onChange={(e) => onPlan("anchor", e.currentTarget.value)}
          >
            {/* Leerer Wert = kein Anker = Handdatum. Im individuellen Plan zulaessig (Spec,
                „Individueller Editor"): fest vereinbarte Kalendertermine sind genau der Fall, fuer den
                ein individueller Plan existiert. */}
            <option value="">Handdatum (kein Anker)</option>
            {(Object.keys(ANKER_LABEL) as (keyof typeof ANKER_LABEL)[]).map((anker) => (
              <option key={anker} value={anker}>
                {ANKER_LABEL[anker]}
              </option>
            ))}
          </select>
          <div className="payment-status-help">Bezugspunkt der Fälligkeit</div>
        </div>
        <div className="payment-status-field" data-permission="manage_payments">
          <span className="payment-status-label">Tage nach Anker{number}</span>
          <Zahlenfeld
            className="mono"
            ariaLabel={`Tage nach Anker der Rate ${index + 1}`}
            step="1"
            wert={(p.dueDays as string) ?? ""}
            gesperrt={planGesperrt}
            sofort={sofort}
            placeholder="0"
            onCommit={(wert) => onPlan("dueDays", wert)}
          />
          <div className="payment-status-help">Negative Werte bedeuten „davor"</div>
        </div>
        <div className="payment-status-field">
          <label>Zahlstatus{number}</label>
          {status === "paid" ? (
            <span className="payment-status-chip">{historisch ? "Historische Zahlung" : "Bezahlt"}</span>
          ) : (
            <select
              value={status}
              disabled={ausfuehrungGesperrt}
              title={ausfuehrungHinweis}
              onChange={(e) => onAusfuehrung("status", e.currentTarget.value)}
            >
              {Object.entries(PAYMENT_STATUSES)
                .filter(([value]) => value !== "paid")
                .map(([value, option]) => (
                  <option value={value} key={value}>
                    {option.label}
                  </option>
                ))}
            </select>
          )}
          <div className="payment-status-help">{statusHinweis(status, p)}</div>
          {kannMarkieren && istAktionen ? <BezahltMarkieren p={p} aktionen={istAktionen} /> : null}
        </div>
        <div className="payment-status-field">
          <label>Dollarkurs{number}</label>
          <Zahlenfeld
            className="mono"
            min="0"
            step="0.0001"
            wert={(p.usdRate as string) ?? ""}
            gesperrt={ausfuehrungGesperrt || historisch}
            placeholder="z. B. 1.0850"
            onCommit={(wert) => onAusfuehrung("usdRate", wert)}
          />
          <div className="payment-status-help">{ausfuehrungHinweis ?? "USD je 1 €"}</div>
        </div>
        {p.actual ? (
          <IstZahlungBlock o={o} p={p} zahlung={p.actual} aktionen={istAktionen} gesperrt={ausfuehrungGesperrt} />
        ) : null}
      </div>
    </div>
  );
}

export interface RatenplanProps {
  /** Die fuer die Anzeige normalisierte Bestellung - siehe `ZahlungTab.tsx`. */
  bestellung: Order;
  /**
   * Ungenutzt. `renderPayments`/`renderPaySum` formatieren Betraege ausschliesslich mit `fmtUsd`
   * (`index.html:9366, 9418`), nie mit `fmtMoney` - die Bestellwaehrung und damit die
   * Lieferantenliste spielt hier keine Rolle. Die Stelle bleibt in der Signatur, damit ein
   * spaeterer Umbau auf `fmtMoney` sie nicht erst wieder einziehen muss.
   */
  lieferanten: readonly Supplier[];
  /** `patchPayment(i, key, value)` (`index.html:9441`) - Status, Bezahlt am, Dollarkurs. */
  onRate: (index: number, feld: keyof OrderPayment, wert: string) => void;
  /** Plan „Nach Plan": Anteil, Betrag, Anker, Tage und Faelligkeit sind nur Anzeige. */
  planGesperrt?: boolean;
  /** Bezahlt markieren, Datum, Rücknahme, Belege - nur mit `manage_payments` gesetzt. */
  istAktionen?: IstZahlungAktionen;
}

/** Die gespeicherten Raten (Plan „Nach Plan" oder Anzeige); Planfelder nur lesbar. */
export function Ratenplan({ bestellung: o, onRate, planGesperrt = true, istAktionen }: RatenplanProps) {
  const ps = ((o.payments as OrderPayment[] | undefined) || []) as OrderPayment[];

  return (
    <>
      <div className="payment-status-list" id="payRows">
        {ps.map((p, i) => (
          <Ratenkarte
            key={(p.id as string) || i}
            p={p}
            index={i}
            anzahl={ps.length}
            o={o}
            planGesperrt={planGesperrt}
            onPlan={() => {}}
            onAusfuehrung={(feld, wert) => onRate(i, feld, wert)}
            istAktionen={istAktionen}
          />
        ))}
      </div>
      <Ratensumme bestellung={o} />
    </>
  );
}

export interface PlanEditorProps {
  bestellung: Order;
  /** Die gespeicherten Raten (leer bei „Kein Zahlungsplan"). */
  gespeichert: readonly OrderPayment[];
  /** Ausgangsentwurf bei „Kein Zahlungsplan" (eine Rate 100 % oder aus „In Raten umwandeln"). */
  start?: readonly EntwurfRate[];
  schreibgeschuetzt?: boolean;
  /**
   * Das Konto darf den Plan nicht aendern (kein `manage_payments`). Wird hier DIREKT ausgewertet, nicht nur
   * ueber den Rechte-Durchgang des Drawers: der laeuft nur beim Neuzeichnen des Drawers, nicht nach einem
   * Zustandswechsel im Reiter. Betrifft Planfelder, Hinzufuegen, Loeschen und Speichern - nie Verwerfen.
   */
  ohnePlanRecht?: boolean;
  /** Status/Dollarkurs einer GESPEICHERTEN Rate - Index in `gespeichert`. */
  onRate: (index: number, feld: keyof OrderPayment, wert: string) => void;
  onSpeichern: (raten: PlanRateEingabe[]) => void;
  /** „Verwerfen" im Zustand „Kein Zahlungsplan": den Entwurf schliessen. Sonst zurueck auf den gespeicherten Stand. */
  onVerwerfen?: () => void;
  /** Wie `RatenplanProps.istAktionen`. */
  istAktionen?: IstZahlungAktionen;
}

/**
 * Der individuelle Editor (Spec, „Individueller Editor"). Der ENTWURF ist lokal; erst „Speichern"
 * schreibt (`onSpeichern` -> `PUT payment-plan`, atomar). Aendert jemand anderes die Planfelder der
 * gespeicherten Raten, wird der Entwurf verworfen und der Nutzer darauf hingewiesen (`planSignatur`).
 * Eigene Aenderungen an Status und Dollarkurs gehen sofort einzeln und entwerten den Entwurf nicht.
 */
export function PlanEditor({
  bestellung: o,
  gespeichert,
  start,
  schreibgeschuetzt,
  ohnePlanRecht,
  onRate,
  onSpeichern,
  onVerwerfen,
  istAktionen,
}: PlanEditorProps) {
  const planSchutz = !!schreibgeschuetzt || !!ohnePlanRecht;
  const [entwurf, setEntwurf] = useState<EntwurfRate[]>(() =>
    start && gespeichert.length === 0 ? [...start] : entwurfAusRaten(gespeichert),
  );
  const [meldung, setMeldung] = useState<string | null>(null);
  const [aktionsgrund, setAktionsgrund] = useState<string | null>(null);

  const signatur = useMemo(() => planSignatur(gespeichert), [gespeichert]);
  const letzteSignatur = useRef(signatur);
  const geaendertVorher = useRef(false);
  const entwurfRef = useRef(entwurf);
  entwurfRef.current = entwurf;
  useEffect(() => {
    if (letzteSignatur.current === signatur) return;
    letzteSignatur.current = signatur;
    // „Kein Zahlungsplan" mit Start-Entwurf: es gibt noch nichts Gespeichertes, das ihn entwerten koennte.
    if (gespeichert.length === 0) return;
    const neu = entwurfAusRaten(gespeichert);
    // Nur eine FREMDE Aenderung ist meldepflichtig: war der Entwurf angefasst und ist der neue Stand
    // nicht das Ergebnis des eigenen Speicherns (gleicher Inhalt), geht die Eingabe verloren.
    setMeldung(
      geaendertVorher.current && !entwurfInhaltGleich(entwurfRef.current, neu)
        ? "Der Plan wurde zwischenzeitlich geändert – der Entwurf wurde auf den gespeicherten Stand zurückgesetzt."
        : null,
    );
    setEntwurf(neu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatur]);

  const nachId = useMemo(() => new Map(gespeichert.filter((p) => p.id).map((p) => [String(p.id), p] as const)), [gespeichert]);
  const statusVon = (r: EntwurfRate): string => {
    const bekannt = r.id !== null ? nachId.get(r.id) : undefined;
    return bekannt ? paymentStatusOf(bekannt) : "open";
  };

  const geaendert = entwurfGeaendert(entwurf, gespeichert);
  // Nach dem Signatur-Effekt oben: er liest den Wert des VORHERIGEN Renders.
  useEffect(() => {
    geaendertVorher.current = geaendert;
  });
  const summe = summenzeile(entwurf);
  const speichernGrund = speichernGesperrtGrund(entwurf);
  const hinzu = rateHinzufuegen(entwurf, statusVon);
  // Altbestand mit ungueltigen Anteilen: den GESPEICHERTEN Stand benennen (Spec, „Altbestand ...").
  const gespeicherteSumme = gespeichert.length > 0 ? summenzeile(gespeichert) : null;

  const aendere = (neu: EntwurfRate[]): void => {
    setEntwurf(neu);
    setMeldung(null);
    setAktionsgrund(null);
  };

  const anzeige = entwurf.map((r) => {
    const bekannt = r.id !== null ? nachId.get(r.id) : undefined;
    const basis: OrderPayment = bekannt
      ? { ...bekannt }
      : { status: "open", paid: false, usdRate: "", paidDate: "" };
    return {
      ...basis,
      id: r.id ?? undefined,
      pct: r.pct,
      amount: r.amount,
      anchor: r.anchor,
      dueDays: r.dueDays,
      due: r.dueDate,
    } as OrderPayment;
  });

  return (
    <>
      {gespeicherteSumme && !gespeicherteSumme.ok ? (
        <div className="plan-abweichung" role="status">
          Der gespeicherte Plan ist nicht vollständig: {gespeicherteSumme.text}. Bitte korrigieren und speichern.
        </div>
      ) : null}
      {meldung ? (
        <div className="plan-hinweis" role="status">
          {meldung}
        </div>
      ) : null}
      <div className="payment-status-list" id="payRows">
        {entwurf.map((r, i) => {
          const rate = anzeige[i];
          const status = statusVon(r);
          const grund = loeschenGesperrtGrund(entwurf, i, statusVon);
          const bekannt = r.id !== null ? nachId.get(r.id) : undefined;
          const anteilNachtragbar = status !== "open" && bekannt?.historisch !== true && !schreibgeschuetzt && bekannt !== undefined && !ohnePlanRecht && anteilFehlt(bekannt.pct);
          return (
            <Ratenkarte
              key={r.schluessel}
              p={rate}
              index={i}
              anzahl={entwurf.length}
              o={o}
              planGesperrt={status !== "open" || planSchutz}
              sofort
              anteilNachtragbar={anteilNachtragbar}
              istAktionen={istAktionen}
              ausfuehrungGesperrt={r.id === null || !!schreibgeschuetzt}
              onPlan={(feld, wert) => aendere(planFeldSetzen(entwurf, i, feld, wert))}
              onAusfuehrung={(feld, wert) => {
                const index = gespeichert.findIndex((g) => g.id !== undefined && String(g.id) === r.id);
                if (index >= 0) onRate(index, feld, wert);
              }}
              loeschen={{
                grund: planSchutz ? "Gesperrt" : grund,
                onClick: () => {
                  const ergebnis = rateEntfernen(entwurf, i, statusVon);
                  if (ergebnis.ok) aendere(ergebnis.raten);
                  else setAktionsgrund(ergebnis.grund);
                },
              }}
            />
          );
        })}
      </div>
      <div className={`plan-summe ${summe.ok ? "ok" : "fehler"}`} id="planSumme" role="status">
        {summe.text}
      </div>
      {aktionsgrund ? <div className="plan-hinweis">{aktionsgrund}</div> : null}
      <div className="plan-aktionen">
        <button
          type="button"
          className="btn-ghost"
          data-permission="manage_payments"
          disabled={planSchutz || !hinzu.ok}
          title={!hinzu.ok ? hinzu.grund : "Hängt eine offene Rate an"}
          onClick={() => {
            if (hinzu.ok) aendere(hinzu.raten);
          }}
        >
          <Ikone name="circle-plus" />
          Rate hinzufügen
        </button>
        <button
          type="button"
          className="btn-primary"
          data-permission="manage_payments"
          disabled={planSchutz || !geaendert || speichernGrund !== null}
          title={speichernGrund ?? (geaendert ? "Speichert den ganzen Plan" : "Keine Änderung")}
          onClick={() => onSpeichern(alsPlanRaten(entwurf, gespeichert))}
        >
          Speichern
        </button>
        <button
          type="button"
          className="btn-ghost"
          disabled={schreibgeschuetzt || (!geaendert && !onVerwerfen)}
          onClick={() => {
            if (gespeichert.length === 0 && onVerwerfen) {
              onVerwerfen();
              return;
            }
            aendere(entwurfAusRaten(gespeichert));
            onVerwerfen?.();
          }}
        >
          Verwerfen
        </button>
      </div>
      <Ratensumme bestellung={o} />
    </>
  );
}

/** `renderPaySum` (`index.html:9398-9419`). */
function Ratensumme({ bestellung: o }: { bestellung: Order }) {
  const ps = ((o.payments as OrderPayment[] | undefined) || []) as OrderPayment[];
  if (!ps.length) {
    return (
      <div className="pay-sum" id="paySum">
        <span>Noch kein Zahlstatus angelegt.</span>
      </div>
    );
  }
  const pctSum = ps.reduce((s, p) => s + (Number(p.pct) || 0), 0);
  const total = ps.reduce((s, p) => s + payAmount(o, p), 0);
  const paidCount = ps.filter((p) => paymentStatusOf(p) === "paid").length;
  const fixedCount = ps.filter((p) => paymentStatusOf(p) === "fixed").length;
  const open =
    total -
    ps.filter((p) => paymentStatusOf(p) === "paid").reduce((s, p) => s + payAmount(o, p), 0);
  const statusSummary =
    paidCount === ps.length
      ? "vollständig bezahlt"
      : [
          paidCount ? `${paidCount} bezahlt` : "",
          fixedCount ? `${fixedCount} fix` : "",
          `${ps.length - paidCount - fixedCount} offen`,
        ]
          .filter(Boolean)
          .join(" · ");
  return (
    <div className="pay-sum" id="paySum">
      <span>
        {statusSummary}
        {ps.length > 1 && pctSum ? ` · ${NUM.format(pctSum)} % verteilt` : ""}
      </span>
      <span>
        offen: <b className={open <= 0.005 ? "ok" : "open"}>{fmtUsd(Math.max(0, open))}</b>
      </span>
    </div>
  );
}
